import {Component, EventEmitter, Input, Output} from '@angular/core';
import {SmContextMenuItemModel} from '../sm-context-menu-item/sm-context-menu-item.component';
import {GlobalSearchContainerResult, GlobalSearchItemResult} from '../../search/global-search.service';
import type {FavoriteSource} from '../../../favorites/favorites.service';
import type {InventoryMenuOptions} from '../sm-inventory-context-menu/sm-inventory-context-menu.component';

@Component({
  selector: 'sm-global-search-results',
  templateUrl: './sm-global-search-results.component.html',
  styleUrls: ['./sm-global-search-results.component.scss'],
  standalone: false,
})
export class SmGlobalSearchResultsComponent {
  @Input() fullSearchLabel = '';
  @Output() fullSearchRequested = new EventEmitter<void>();
  @Input() inline = false;
  @Input() isOpen = false;
  @Input() query = '';
  @Input() highlightQuery = '';
  @Input() isLoading = false;
  @Input() error = '';
  @Input() title = '';
  @Input() closeLabel = '';
  @Input() backLabel = '';
  @Input() loadingLabel = '';
  @Input() retryLabel = '';
  @Input() limitLabel = '';
  @Input() emptyTitle = '';
  @Input() emptyDescriptionTemplate = '';
  @Input() errorPrefix = '';
  @Input() containersTitle = '';
  @Input() itemsTitle = '';
  @Input() itemSections: {title: string; items: GlobalSearchItemResult[]}[] = [];
  @Input() containerMenuItems: SmContextMenuItemModel[] = [];
  @Input() containerInventoryMenuSource: FavoriteSource | null = null;
  @Input() itemMenuItems: SmContextMenuItemModel[] = [];
  @Input() containerEmptyDescriptionLabel = '';
  @Input() itemEmptyDescriptionLabel = '';
  @Input() containerOpenLabelTemplate = '';
  @Input() itemOpenLabelTemplate = '';
  @Input() containerMenuAriaLabelTemplate = '';
  @Input() containerCurrentLabel = '';
  @Input() itemMenuAriaLabelTemplate = '';
  @Input() rootLocationLabel = '';
  @Input() hasMoreContainers = false;
  @Input() hasMoreItems = false;
  @Input() relocationMode = false;
  @Input() relocationSourceKind: 'container' | 'item' | null = null;
  @Input() relocationSourceId = '';

  @Output() containerOpen = new EventEmitter<GlobalSearchContainerResult>();
  @Output() itemOpen = new EventEmitter<GlobalSearchItemResult>();
  @Output() closeRequested = new EventEmitter<void>();
  @Output() backRequested = new EventEmitter<void>();
  @Output() retryRequested = new EventEmitter<void>();
  @Output() containerMenuItemSelect = new EventEmitter<{
    item: SmContextMenuItemModel;
    result: GlobalSearchContainerResult;
  }>();
  @Output() itemMenuItemSelect = new EventEmitter<{
    item: SmContextMenuItemModel;
    result: GlobalSearchItemResult;
  }>();

  trackById(_: number, item: {id: string}): string {
    return item.id;
  }

  handleContainerOpen(result: GlobalSearchContainerResult): void {
    if (this.relocationMode) {
      return;
    }

    this.containerOpen.emit(result);
  }

  handleItemOpen(result: GlobalSearchItemResult): void {
    this.itemOpen.emit(result);
  }

  handleContainerMenuItemSelect(result: GlobalSearchContainerResult, item: SmContextMenuItemModel): void {
    this.containerMenuItemSelect.emit({
      item,
      result,
    });
  }

  handleItemMenuItemSelect(result: GlobalSearchItemResult, item: SmContextMenuItemModel): void {
    this.itemMenuItemSelect.emit({
      item,
      result,
    });
  }

  requestClose(): void {
    if (this.relocationMode) {
      return;
    }

    this.closeRequested.emit();
  }

  requestBack(): void {
    if (!this.relocationMode) this.backRequested.emit();
  }

  getContainerRelocationTargetId(result: GlobalSearchContainerResult): string | null {
    if (
      !this.relocationMode
      || result.canEdit !== true
      || result.isCurrent
      || (this.relocationSourceKind === 'container' && result.id === this.relocationSourceId)
    ) {
      return null;
    }

    return result.id;
  }

  getContainerInventoryMenu(result: GlobalSearchContainerResult): InventoryMenuOptions | null {
    if (!this.containerInventoryMenuSource || this.relocationMode) {
      return null;
    }

    return {
      canEdit: result.canEdit === true,
      source: this.containerInventoryMenuSource,
    };
  }

  formatTemplate(template: string, values: Record<string, string>): string {
    if (!template.trim()) {
      return '';
    }

    return Object.entries(values).reduce(
      (current, [key, value]) => current.split(`{{${key}}}`).join(value),
      template,
    );
  }

  get hasResults(): boolean {
    return this.containers.length > 0 || this.displayedItemSections.length > 0;
  }

  get displayedItemSections(): {title: string; items: GlobalSearchItemResult[]}[] {
    return this.itemSections.length ? this.itemSections.filter(section => section.items.length)
      : this.items.length ? [{title: this.itemsTitle, items: this.items}] : [];
  }

  get containers(): GlobalSearchContainerResult[] {
    return this._containers;
  }

  @Input()
  set containers(value: GlobalSearchContainerResult[]) {
    this._containers = Array.isArray(value) ? value : [];
  }

  get items(): GlobalSearchItemResult[] {
    return this._items;
  }

  @Input()
  set items(value: GlobalSearchItemResult[]) {
    this._items = Array.isArray(value) ? value : [];
  }

  private _containers: GlobalSearchContainerResult[] = [];
  private _items: GlobalSearchItemResult[] = [];
}
